import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import { useEcommApp, useHeadlessApp } from '@jane/shared/providers';

import { configureCustomerStore } from './redux';

// We use a ref to pass various values to redux thunk actions
// as extra arguments. This allows us to access these values in thunks without
// needing to pass them as props or store them in Redux state.
//
// The ref approach is required because:
// 1. Redux stores should never be recreated
// 2. If we passed these values directly, changing them would recreate the store
//    (causing state loss, broken subscriptions, and re-running side effects)
// 3. The ref stays stable while its `.current` property can be updated
// 4. Thunks access the latest values via `extraArgsRef.current.appMode`
//
// This is a transitional pattern as we migrate away from Redux to providers/hooks.

export const ConfigureStore = ({ children }: PropsWithChildren) => {
  const { appMode } = useEcommApp();
  const { headlessStoreId } = useHeadlessApp();
  const disableGaInBloomHeadless = useFlag(
    FLAGS.c2DisableGaInBloomHeadless,
    false
  );
  const extraArgsRef = useRef({
    appMode,
    disableGaInBloomHeadless,
    headlessStoreId,
  });

  useEffect(() => {
    extraArgsRef.current = {
      appMode,
      disableGaInBloomHeadless,
      headlessStoreId,
    };
  }, [appMode, disableGaInBloomHeadless, headlessStoreId]);

  const reduxStore = useMemo(
    () => configureCustomerStore({}, extraArgsRef),
    []
  );

  return <ReduxProvider store={reduxStore}>{children}</ReduxProvider>;
};
